<template>
  <div class="w-full animate-fade-in-up">
    <!-- POPUPS -->
    <vs-popup
      class="calendar-popup items-center"
      :title="$t('skillDetails.details.d5')"
      :active.sync="popupActive"
    >
      <div class="flex justify-center items-center w-full py-4">
        <flat-pickr
          v-model="update_training_duration"
          :config="{ inline: true, dateFormat: 'Y-m-d', minDate:training_duration}"
          class="custom-flatpickr"
          name="trainingDuration"
        />
      </div>
    </vs-popup>

    <!-- Popup for mark goal complete (Coach side) -->
    <vs-popup :title="$t('skillDetails.action2.a15')" :active.sync="markGoalCompletedConfirmation" >
      <div class="con-exemple-prompt p-4">
        <div class="mb-6 text-gray-700 font-medium text-lg">
          {{$t('skillDetails.action2.a16')}}
        </div>
        <div class="flex justify-end">
          <vs-button
            class="shadow-lg rounded-xl font-bold px-6 py-2"
            @click="markGoalComplete()"
          >{{$t('skillDetails.action2.a17')}}</vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Submit Proof of Work Popup (Founder/Trainee Side) -->
    <vs-popup title="Submit Proof of Work" :active.sync="submitProofPopup">
      <div class="p-4 space-y-5">
        <p class="text-sm text-gray-600 mb-4">
          Upload a document or provide a link to prove your progress. Your coach will review this to mark the goal as complete.
        </p>
        
        <!-- File Upload -->
        <div>
          <label class="block text-sm font-bold text-gray-700 mb-2">Upload Document</label>
          <input 
            type="file" 
            @change="handleFileUpload" 
            class="w-full border border-gray-300 rounded-lg p-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" 
          />
        </div>

        <!-- External Link -->
        <div>
          <label class="block text-sm font-bold text-gray-700 mb-2">External Link (Optional)</label>
          <vs-input class="w-full" v-model="powLink" placeholder="https://..." />
        </div>

        <!-- Notes -->
        <div>
          <label class="block text-sm font-bold text-gray-700 mb-2">Notes to Coach</label>
          <vs-textarea class="w-full" v-model="powNotes" rows="3" placeholder="Describe what you achieved..."></vs-textarea>
        </div>

        <div class="flex justify-end mt-6">
          <vs-button 
            class="shadow-lg rounded-xl font-bold px-8 py-3" 
            :disabled="isUploading || (!powFile && !powLink)" 
            @click="submitProof()"
          >
            {{ isUploading ? 'Submitting...' : 'Submit to Coach' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Proof Submitted Success Popup -->
    <vs-popup title="Under Review" :active.sync="proofSubmittedPopup" :button-close-hidden="false">
      <div class="p-6 text-center">
        <feather-icon icon="CheckCircleIcon" svgClasses="text-success w-20 h-20 mx-auto mb-6 opacity-90"></feather-icon>
        <h3 class="text-2xl font-extrabold text-gray-800 mb-3">Proof Submitted!</h3>
        <p class="text-gray-600 font-medium mb-8">
          Your assigned coach will review your uploaded documents. Once approved, your goal will be marked as complete.
        </p>
        <div class="w-full flex justify-center">
          <vs-button class="rounded-xl shadow-lg font-bold px-8 py-3" @click="proofSubmittedPopup = false" size="large">
            Got it
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <vs-popup
      class="holamundo"
      :title="$t('skillDetails.endorsement.e1')"
      :active.sync="goalCompletedPopup"
      :button-close-hidden="false" 
    >
      <div class="p-4">
        <div class="mb-6 text-center">
            <span class="text-center font-extrabold" style="font-size: 24px; color: #28C76F;">
              {{ $t('skillDetails.endorsement.e2') }}
            </span>
        </div>
        <div class="mt-2 mb-8 flex justify-center">
          <img
            src="@/assets/Illustrations/Ill_History.svg"
            alt="send endorsement invite"
            class="w-48 h-48 transition-transform duration-500 hover:scale-105"
          />
        </div>
        <div class="w-full flex justify-center">
          <vs-button class="rounded-xl shadow-lg font-bold px-8 py-3" @click="addAnotherSkill" size="large">
            {{ $t('skillDetails.endorsement.e3') }}
          </vs-button>
        </div>
      </div>
    </vs-popup>
    <!-- End Popups -->

    <!-- ACCORDION CONTAINER -->
    <vx-card-mod
      ref="accordionCard"
      class="mb-base modern-accordion-card"
      :title="$t('skillDetails.details.d0')"
      :collapse-action="true"
      :is-content-collapsed="true"
      :max-height="null"
    >
      
      <!-- The Progress Bar appears inside this slot -->
      <div class="w-full mb-12 mt-2 px-4 sm:px-8">
        <slot></slot>
      </div>

      <div class="space-y-8 text-left px-2 sm:px-4">
        
        <!-- INITIAL LEVEL SECTION -->
        <div>
          <h3 class="text-xs font-extrabold text-gray-400 uppercase tracking-widest mb-3 ml-2">{{ $t("skillDetails.details.d1") }}</h3>
          <div class="bg-gray-50 border border-gray-100 rounded-3xl p-5 sm:p-6 flex items-center shadow-inner transition-colors duration-300 hover:bg-gray-100">
            <span class="font-extrabold text-lg text-gray-800 uppercase tracking-wide">
              {{ your_initial_level }} - {{ initial_level_text }}
            </span>
          </div>
        </div>

        <!-- DURATION SECTION -->
        <div>
          <h3 class="text-xs font-extrabold text-gray-400 uppercase tracking-widest mb-3 ml-2">{{ $t("skillDetails.details.d2") }}</h3>
          <div class="bg-gray-50 border border-gray-100 rounded-3xl p-5 sm:p-6 flex items-center justify-between shadow-inner transition-colors duration-300 hover:bg-gray-100 group">
            
            <div class="flex-grow text-left">
              <h4 class="font-bold text-gray-800 text-lg mb-1">{{ $t("skillDetails.details.d3") }}</h4>
              <div class="flex items-center space-x-2 text-gray-600 font-medium">
                <span>{{ formatDate(startDate) }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                <span>{{ formatDate(training_duration) }}</span>
              </div>
            </div>
            
            <!-- Edit Icon -->
            <div class="flex-shrink-0 ml-4 cursor-pointer p-3 rounded-full text-gray-400 hover:text-primary hover:bg-primary/10 transition-all duration-300" @click="showCustomCalendar">
              <feather-icon icon="Edit3Icon" svgClasses="h-6 w-6"></feather-icon>
            </div>

          </div>
        </div>

        <!-- PROGRESS MEASURES SECTION -->
        <div>
          <h3 class="text-xs font-extrabold text-gray-400 uppercase tracking-widest mb-4 ml-2">{{ $t("skillDetails.details.d4") }}</h3>
          
          <div class="flex flex-col space-y-4">
            <div
              v-for="(p, indexp) in progress_measures_list"
              :key="indexp"
              class="bg-white border border-gray-100 rounded-3xl p-5 sm:p-6 flex items-center shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
            >
              
              <div class="flex-shrink-0 mr-5 sm:mr-6">
                <span class="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 shadow-sm">
                  <img
                    :src="require('@/assets/Iconography/Progress measures/PM_' + ((parseInt(indexp) % 10) + 1) + '.svg')"
                    alt="pm"
                    class="w-8 h-8 opacity-80 transition-transform duration-300 hover:scale-110"
                    style="mix-blend-mode: multiply;"
                  />
                </span>
              </div>
              
              <div class="flex-grow text-left">
                <h4 v-if="p.title" class="font-bold text-gray-800 text-lg mb-1 leading-snug">
                  {{ p.title }}
                </h4>
                <p class="text-sm sm:text-base font-medium text-gray-600 m-0 leading-relaxed">
                  {{ p.pmMap }}
                </p>
              </div>

            </div>
          </div>
        </div>

        <!-- MARK GOAL COMPLETE BUTTON -->
        <div class="pt-8 pb-4 flex justify-center">
          
          <!-- Already Complete -->
          <vs-button
            v-if="progressStatus === 'complete'"
            disabled
            size="large"
            color="#111827" 
            class="rounded-xl shadow-lg font-extrabold px-10 py-4 w-full sm:w-auto"
          >
            {{ $t('skillDetails.endorsement.e1') }}
          </vs-button>

          <!-- New State: Under Review -->
          <vs-button
            v-else-if="progressStatus === 'under_review'"
            disabled
            size="large"
            color="#F59E0B" 
            class="rounded-xl shadow-lg font-extrabold px-10 py-4 w-full sm:w-auto text-white"
          >
            Proof Under Review
          </vs-button>
          
          <!-- Trainee Side: Submit Proof of Work -->
          <vs-button
            v-else-if="progressStatus === 'in_progress' && userType === 'trainee'"
            size="large"
            @click="submitProofPopup = true"
            color="#111827"
            class="rounded-xl shadow-lg font-extrabold px-10 py-4 hover-lift w-full sm:w-auto"
          >
            Submit Proof of Work
          </vs-button>
          
          <!-- Coach Side: Bypass and mark directly (if allowed) -->
          <vs-button
            v-else-if="progressStatus === 'in_progress' && userType === 'coach' && terminology==2"
            size="large"
            @click="markGoalConfirmComplete"
            color="#111827"
            class="rounded-xl shadow-lg font-extrabold px-10 py-4 hover-lift w-full sm:w-auto"
          >
            {{ $t('skillDetails.endorsement.e7') }}
          </vs-button>

        </div>

      </div>
    </vx-card-mod>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import flatpickr from 'flatpickr'
import {Spanish} from "flatpickr/dist/l10n/es";
if(window.$cookies.get('locale') ==='es'){
  flatpickr.localize(Spanish);
}

import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  name: "Details",
  props: ["terminology"],
  data() {
    return {
      userType: this.$store.state.AppActiveUser.userType,
      progress_measures_list: [],
      custom_progress_measures: [],
      your_initial_level: "",
      initial_level_text: "",
      training_duration: "",
      update_training_duration:"",
      custom_duration: null, 
      popupActive: false,
      startDate:"",
      CCD: this.$t("addNewSkill.data"),
      markGoalCompletedConfirmation:false,
      goalCompletedPopup:false,
      progressStatus:"",

      // Proof of Work New State variables
      submitProofPopup: false,
      proofSubmittedPopup: false,
      powFile: null,
      powLink: "",
      powNotes: "",
      isUploading: false,
    };
  },
  components: {
    Datepicker,
    flatPickr
  },
  mounted() {
    // Locate the header element of the card
    const header = this.$el.querySelector('.vx-card__header');
    if (header) {
      header.style.cursor = 'pointer';
      header.style.userSelect = 'none'; 
      
      header.addEventListener('click', (e) => {
        const toggleBtn = header.querySelector('.vx-card__action-buttons .feather-icon, .vx-card__action-buttons svg, .vx-card__action-buttons i, .vx-card__action-buttons span');
        if (toggleBtn) {
          if (toggleBtn.contains(e.target)) return;
          toggleBtn.click();
        }
      });
    }
  },
  watch: {
    update_training_duration: function(o, n) {
      this.storeCustomDuration(n);
    }
  },
  methods: {
    // --- File Upload & Proof of Work Submission Methods --- //
    handleFileUpload(event) {
      this.powFile = event.target.files[0];
    },
    
    async submitProof() {
      if (!this.powFile && !this.powLink) return;
      this.isUploading = true;

      try {
        let uploadedFileUrl = "";
        const traineeCompetenciesId = this.$route.params.traineeCompetenciesId;
        
        // 1. If user uploaded a physical file, push it to the Python/FastAPI backend
        if (this.powFile) {
          const formData = new FormData();
          formData.append("file", this.powFile);
          
          // Use env variable for Python API URL, otherwise fallback to localhost
          const pythonApiUrl = 'https://parser.ai.getboarded.com';
          
          const response = await fetch(`${pythonApiUrl}/api/upload-proof-file`, {
            method: "POST",
            body: formData,
          });

          const data = await response.json();
          if (data.status === "success") {
            uploadedFileUrl = data.file_url;
          } else {
            throw new Error("File upload failed on the server.");
          }
        }

        // 2. Submit the records to the feathers `proof-of-work` service
        await this.$store.dispatch("proof-of-work/create", {
          traineeCompetenciesId: traineeCompetenciesId,
          fileUrl: uploadedFileUrl,
          externalLink: this.powLink,
          notes: this.powNotes,
          status: "pending" 
        });

        // 3. Update the overarching Goal/Plan status to under_review
        await this.$store.dispatch("trainee-competencies/patch", [
          traineeCompetenciesId,
          { progressStatus: "under_review" }
        ]);

        // 4. Reset states & Trigger Success UI
        this.progressStatus = "under_review";
        this.submitProofPopup = false;
        this.proofSubmittedPopup = true;
        
        // Reset Inputs
        this.powFile = null;
        this.powLink = "";
        this.powNotes = "";
        
      } catch (error) {
        console.error("Error submitting proof of work: ", error);
        this.$vs.notify({
          title: "Submission Error",
          text: "Could not submit your proof. Please try again.",
          color: "danger",
          position: "top-right"
        });
      } finally {
        this.isUploading = false;
      }
    },
    // --- End Proof of Work logic --- //

    _searchInDataset(dataset, targetId) {
      if (!dataset || !targetId) return null;
      let parts = targetId.split("-");
      for (let key in dataset) {
        if (dataset[key] && dataset[key].competencies) {
          let comps = dataset[key].competencies;
          if (Array.isArray(comps)) {
            let found = comps.find(c => c.id === targetId);
            if (found) return found;
          } else {
            for (let k in comps) {
              if (comps[k] && comps[k].id === targetId) return comps[k];
              if (key === parts[0] && k === parts[1]) return comps[k];
            }
          }
        }
      }
      return null;
    },
    getSkillData(targetId) {
      if (!targetId) return null;
      let parts = targetId.split("-");
      if (parts.length >= 3) {
        targetId = `${parts[0]}-${parts[1]}`;
      }

      let locale = window.$cookies.get('locale') || (this.$i18n && this.$i18n.locale) || 'en';
      if (locale.includes('-')) {
        locale = locale.split('-')[0];
      }

      let patterns = [];
      if (targetId.startsWith('eg-') || targetId.startsWith('nb-') || targetId.startsWith('ei-') || targetId.startsWith('es-')) {
        patterns.push(`i18n-${locale}-deep-Data`);
      } else if (targetId.startsWith('sc-')) {
        patterns.push(`i18n-${locale}-pci-Data-Coach`, `i18n-${locale}-pci-Data`);
      } else if (targetId.startsWith('lt-') || targetId.startsWith('l-')) {
        patterns.push(`i18n-${locale}-Data-DPM`);
      } else if (targetId.startsWith('pt-')) {
        patterns.push(`i18n-${locale}-peopletree-Data`);
      }

      const allPatterns = [
        `i18n-${locale}-deep-Data`,
        `i18n-${locale}-pci-Data-Coach`,
        `i18n-${locale}-pci-Data`,
        `i18n-${locale}-peopletree-Data`,
        `i18n-${locale}-Data-DPM`,
        `i18n-${locale}-Data`
      ];

      allPatterns.forEach(p => {
        if (!patterns.includes(p)) patterns.push(p);
      });

      if (locale !== 'en') {
        let enPatterns = [];
        if (targetId.startsWith('eg-') || targetId.startsWith('nb-') || targetId.startsWith('ei-') || targetId.startsWith('es-')) {
          enPatterns.push('i18n-en-deep-Data');
        } else if (targetId.startsWith('sc-')) {
          enPatterns.push('i18n-en-pci-Data-Coach', 'i18n-en-pci-Data');
        } else if (targetId.startsWith('lt-') || targetId.startsWith('l-')) {
          enPatterns.push('i18n-en-Data-DPM');
        } else if (targetId.startsWith('pt-')) {
          enPatterns.push('i18n-en-peopletree-Data');
        }

        const allEnPatterns = [
          'i18n-en-deep-Data',
          'i18n-en-pci-Data-Coach',
          'i18n-en-pci-Data',
          'i18n-en-peopletree-Data',
          'i18n-en-Data-DPM',
          'i18n-en-Data'
        ];

        allEnPatterns.forEach(p => {
          if (!enPatterns.includes(p) && !patterns.includes(p)) enPatterns.push(p);
        });
        patterns = patterns.concat(enPatterns);
      }

      for (let i = 0; i < patterns.length; i++) {
        try {
          const fallbackModule = require(`@/i18n/${patterns[i]}.js`);
          const fallbackData = fallbackModule.default || fallbackModule;
          if (fallbackData && fallbackData.addNewSkill && fallbackData.addNewSkill.data) {
            let found = this._searchInDataset(fallbackData.addNewSkill.data, targetId);
            if (found) return found;
          }
        } catch (err) {}
      }

      return this._searchInDataset(this.CCD, targetId);
    },
    getProgressMeasureData(progressMeasureId) {
      if (!progressMeasureId) return null;
      let parts = progressMeasureId.split("-");
      if (parts.length < 2) return null;
      let competencyId = `${parts[0]}-${parts[1]}`;

      let competency = this.getSkillData(competencyId);
      if (!competency || !competency.progressMeasures) return null;

      let measures = competency.progressMeasures;
      if (Array.isArray(measures)) {
        return measures.find(m => m.id === progressMeasureId) || null;
      } else {
        if (measures[parts[2]]) return measures[parts[2]];
        for (let k in measures) {
          if (measures[k] && (measures[k].id === progressMeasureId || k === parts[2])) {
            return measures[k];
          }
        }
      }
      return null;
    },
    showCustomCalendar(){
       this.popupActive=true;
    },
    storeCustomDuration() {
      const data ={
        trainingDuration: this.update_training_duration
      }
      this.$store
        .dispatch("trainee-competencies/patch",[this.$route.params.traineeCompetenciesId,data])
        .then(res => {
          window.location.reload();
        })
        .catch(err => {
          console.log("could not set training duration", err);
        });
    },
    formatDate(createdAt) {
      if (!createdAt) return "";
      let date = new Date(createdAt);
      let year = date.getFullYear();
      let month = String(date.getMonth() + 1).padStart(2, '0');
      let day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    },
    addAnotherSkill() {
      this.goalCompletedPopup = false;
      this.$router.push({name: "add-skill"});
    },
    markGoalConfirmComplete(){
      this.markGoalCompletedConfirmation=true;
      this.goalCompletedPopup = false;
    },
    markGoalComplete() {
      this.markGoalCompletedConfirmation = false;
      this.goalCompletedPopup = true;
      this.progressStatus = "complete"; 
      
      const id = this.$route.params.traineeCompetenciesId;
      const goalCompleteObj = { id, progressStatus: "complete" };
      this.$store
          .dispatch("endorsementInvite/markGoalComplete", goalCompleteObj)
          .then(res => {
            const query={query:{traineeCompetenciesId:id}}
            this.$store
                .dispatch("endorsementInvite/fetchInvitees", query)
                .then(res => {
                  for (let i=0;i<res.data.length;i++) {
                    this.$store
                        .dispatch("endorsement-invite-prgms/find",{query:{endorsementInviteId:res.data[i].id}})
                        .then(res => {
                          for (let j=0;j<res.data.length;j++) {
                            this.$store
                                .dispatch("external-feedback/find", {query: {endorsementInvitePrgmsId: res.data[j].id, emailStatus:{$ne:4}}})
                                .then(res1 => {
                                  let deletedexternalToken = [];
                                  for (let k=0;k<res1.data.length;k++) {
                                    if((new Date(res1.data[k].feedbackSentDate)) > (new Date())) {
                                      deletedexternalToken.push(res1.data[k].externalToken);
                                    }
                                  }
                                  const query = {query: {externalToken: deletedexternalToken}}
                                  this.$store
                                      .dispatch("invitee-message-id/find", query)
                                      .then(res => {
                                        const gapFeedObj = {
                                          flag: 'MarkGoalCompleted',
                                          feedbackId: res1.data[j].id,
                                          messageId: res.data
                                        }
                                        this.$store
                                            .dispatch("external-feedback/patch", [res1.data[j].id, gapFeedObj])
                                            .then(res2 => {
                                              this.$store
                                                  .dispatch("external-feedback/find", {query: {endorsementInvitePrgmsId: res2.endorsementInvitePrgmsId}})
                                                  .then(res3 => {
                                                    for (let j = 0; j < res3.data.length; j++) {
                                                      if ((new Date(res3.data[j].feedbackSentDate)) > (new Date())) {
                                                        const ZeroGapData = {
                                                          emailStatus: 6,
                                                          externalTokenExpired:1
                                                        }
                                                        this.$store
                                                            .dispatch("external-feedback/patch", [res3.data[j].id, ZeroGapData])
                                                            .then(res3 => {})
                                                            .catch(err => {});
                                                      }
                                                    }
                                                  })
                                                  .catch(err => {});
                                            })
                                            .catch(err => {});
                                      })
                                      .catch(err => {});
                                })
                                .catch(err => {});
                          }
                        })
                        .catch(err => {});
                  }
                })
                .catch(err => {});
            this.goalCompletedPopup = true;
          })
          .catch(err => {});
    },
  },
  created() {
    const id = this.$route.params.traineeCompetenciesId;
    
    this.$store
      .dispatch("trainee-competencies-prgms/find", {
        query: { traineeCompetenciesId: id }
      })
      .then(res3 => {
        for (const pm in res3.data) {
          let currentMeasure = res3.data[pm];

          if (currentMeasure.progressMeasureId && currentMeasure.progressMeasureId.includes('custom')) {
            this.progress_measures_list.push({
              id: currentMeasure.progressMeasureId,
              pm: currentMeasure.progressMeasure,
              pmMap: currentMeasure.progressMeasure,
              title: ""
            });
          } else {
            let foundMeasure = this.getProgressMeasureData(currentMeasure.progressMeasureId);
            if (foundMeasure) {
              this.progress_measures_list.push({
                id: currentMeasure.progressMeasureId,
                pm: foundMeasure.pm || currentMeasure.progressMeasure || "Progress Measure",
                pmMap: foundMeasure.pmMap || foundMeasure.pm || currentMeasure.progressMeasure || "Progress Measure",
                title: foundMeasure.title || ""
              });
            } else {
              this.progress_measures_list.push({
                id: currentMeasure.progressMeasureId,
                pm: currentMeasure.progressMeasure || "Progress Measure",
                pmMap: currentMeasure.progressMeasure || "Progress Measure",
                title: ""
              });
            }
          }
        }
      })
      .catch(err3 => {
        console.log("could not find progress measure", err3);
      });

    this.$store
      .dispatch("trainee-competencies/get", id)
      .then(res => {
        this.your_initial_level = res.initialLevel;
        if (this.your_initial_level > 0 && this.your_initial_level <= 2) {
          this.initial_level_text = this.$t("rateYourLevel.ryl4")
        } else if (this.your_initial_level > 2 && this.your_initial_level <= 5) {
          this.initial_level_text = this.$t("rateYourLevel.ryl5")
        } else if (this.your_initial_level > 5 && this.your_initial_level <= 7) {
          this.initial_level_text = this.$t("rateYourLevel.ryl6")
        } else if(this.your_initial_level > 7 && this.your_initial_level < 10){
          this.initial_level_text = this.$t("rateYourLevel.ryl7")
        } else if(this.your_initial_level === 10){
          this.initial_level_text = this.$t("rateYourLevel.ryl8")
        }
        this.startDate =res.createdAt
        this.training_duration = res.trainingDuration;
        this.progressStatus = res.progressStatus;
      })
      .catch(err => {
        console.log("could not find trainee competency", err);
      });
  }
};
</script>

<style scoped>
/* Base Animations */
.animate-fade-in-up {
  animation: fadeInDown 0.5s cubic-bezier(0.25, 1, 0.5, 1) forwards;
  opacity: 0;
}
@keyframes fadeInDown {
  0% { opacity: 0; transform: translateY(15px); }
  100% { opacity: 1; transform: translateY(0); }
}

.hover-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.hover-lift:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px -5px rgba(17, 24, 39, 0.3) !important; 
}

/* Modifying Custom Card */
.modern-accordion-card {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02) !important;
  border-radius: 1.5rem !important;
  border: 1px solid #F3F4F6 !important; 
}
.modern-accordion-card /deep/ .vx-card__header {
  padding: 1.5rem !important;
  border-bottom: 1px solid #F3F4F6 !important;
}
.modern-accordion-card /deep/ .vx-card__title h4 {
  font-weight: 800 !important;
  font-size: 1.25rem !important;
  color: #1F2937 !important; 
}

/* -------------------------------------------
   PREMIUM FLATPICKR STYLING 
---------------------------------------------*/
input.custom-flatpickr,
.custom-flatpickr.flatpickr-input {
  display: none !important;
}

.calendar-popup /deep/ .vs-popup {
  border-radius: 1.5rem !important;
  width: 360px !important; 
}

.calendar-popup /deep/ .flatpickr-calendar.inline {
  box-shadow: none !important;
  border: none !important;
  margin: 0 auto;
  padding: 0;
  top: 0 !important;
}

.calendar-popup /deep/ .flatpickr-day.selected,
.calendar-popup /deep/ .flatpickr-day.startRange,
.calendar-popup /deep/ .flatpickr-day.endRange,
.calendar-popup /deep/ .flatpickr-day.selected.inRange,
.calendar-popup /deep/ .flatpickr-day.startRange.inRange,
.calendar-popup /deep/ .flatpickr-day.endRange.inRange,
.calendar-popup /deep/ .flatpickr-day.selected:focus,
.calendar-popup /deep/ .flatpickr-day.startRange:focus,
.calendar-popup /deep/ .flatpickr-day.endRange:focus,
.calendar-popup /deep/ .flatpickr-day.selected:hover,
.calendar-popup /deep/ .flatpickr-day.startRange:hover,
.calendar-popup /deep/ .flatpickr-day.endRange:hover {
  background: rgba(var(--vs-primary), 1) !important;
  border-color: rgba(var(--vs-primary), 1) !important;
  font-weight: bold;
  color: white !important;
}

.calendar-popup /deep/ .flatpickr-day:hover {
  background: rgba(var(--vs-primary), 0.1);
  border-color: transparent;
}
</style>